/**
 * Modern Print Utilities
 * Helper functions for elegant document printing
 */

/**
 * Trigger print with optional callback
 * @param {string} documentId - ID of the printable element
 * @param {Function} onBeforePrint - Optional callback before print
 * @param {Function} onAfterPrint - Optional callback after print
 */
export const printDocument = (documentId = null, onBeforePrint = null, onAfterPrint = null) => {
  if (onBeforePrint) onBeforePrint();

  // Small delay to ensure DOM is ready
  setTimeout(() => {
    window.print();
    if (onAfterPrint) {
      // Listen for afterprint event
      window.addEventListener('afterprint', onAfterPrint, { once: true });
    }
  }, 100);
};

/**
 * Export document to Word format (basic HTML to .doc)
 * @param {string} documentId - ID of the element to export
 * @param {string} filename - Name for the downloaded file
 */
export const exportToWord = (documentId, filename = 'document') => {
  const element = document.getElementById(documentId);
  if (!element) {
    console.error(`Element with ID '${documentId}' not found`);
    return;
  }

  // Clone the element to avoid modifying the original
  const clone = element.cloneNode(true);

  // Remove any no-print elements
  const noPrintElements = clone.querySelectorAll('.no-print, .print-hide');
  noPrintElements.forEach(el => el.remove());

  const htmlContent = `
    <!DOCTYPE html>
    <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word'>
    <head>
      <meta charset='utf-8'>
      <title>${filename}</title>
      <style>
        body {
          font-family: 'Times New Roman', serif;
          font-size: 12pt;
          line-height: 1.6;
          margin: 2cm;
        }
        h1 {
          font-size: 18pt;
          font-weight: bold;
          text-align: center;
          margin-bottom: 12pt;
        }
        h2 {
          font-size: 14pt;
          font-weight: bold;
          margin: 10pt 0;
        }
        p {
          margin: 8pt 0;
          text-align: justify;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin: 10pt 0;
        }
        th, td {
          border: 1pt solid black;
          padding: 6pt;
        }
        th {
          background-color: #f0f0f0;
          font-weight: bold;
        }
        .signature-section {
          margin-top: 30pt;
        }
        .text-center {
          text-align: center;
        }
        .text-right {
          text-align: right;
        }
        .font-bold {
          font-weight: bold;
        }
      </style>
    </head>
    <body>
      ${clone.innerHTML}
    </body>
    </html>
  `;

  const blob = new Blob(['\ufeff', htmlContent], {
    type: 'application/msword'
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.doc`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Format date for Indonesian locale
 * @param {string|Date} date - Date to format
 * @param {object} options - Intl.DateTimeFormat options
 */
export const formatDate = (date, options = {}) => {
  const defaultOptions = {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  };

  return new Date(date).toLocaleDateString('id-ID', {
    ...defaultOptions,
    ...options
  });
};

/**
 * Format date with location prefix
 * @param {string|Date} date - Date to format
 * @param {string} location - Location name (default: Jakarta)
 */
export const formatDateWithLocation = (date, location = 'Jakarta') => {
  return `${location}, ${formatDate(date)}`;
};

/**
 * Create printable content wrapper
 * @param {string} content - HTML content
 * @param {string} className - Additional CSS classes
 */
export const createPrintableWrapper = (content, className = '') => {
  return `
    <div class="printable-document ${className}">
      ${content}
    </div>
  `;
};

/**
 * Generate document number
 * @param {string} prefix - Document prefix (e.g., 'SPK', 'SL')
 * @param {number} number - Sequential number
 * @param {string} suffix - Optional suffix
 */
export const generateDocumentNumber = (prefix, number, suffix = '') => {
  const paddedNumber = String(number).padStart(4, '0');
  const month = new Date().toLocaleDateString('id-ID', { month: '2-digit' });
  const year = new Date().getFullYear();

  return `${prefix}/${paddedNumber}/${month}/${year}${suffix ? '/' + suffix : ''}`;
};

/**
 * Prepare document for print (add necessary classes and attributes)
 * @param {string} documentId - ID of the document element
 */
export const prepareDocumentForPrint = (documentId) => {
  const element = document.getElementById(documentId);
  if (!element) return;

  // Add print-ready class
  element.classList.add('print-content', 'force-visible');

  // Mark parent containers
  let parent = element.parentElement;
  while (parent && parent !== document.body) {
    parent.classList.add('print-parent');
    parent = parent.parentElement;
  }
};

/**
 * Clean up after print (remove temporary classes)
 * @param {string} documentId - ID of the document element
 */
export const cleanupAfterPrint = (documentId) => {
  const element = document.getElementById(documentId);
  if (!element) return;

  // Remove print-ready classes
  const printParents = document.querySelectorAll('.print-parent');
  printParents.forEach(el => el.classList.remove('print-parent'));
};

/**
 * Get print preview mode status
 */
export const isPrintPreview = () => {
  return window.matchMedia('print').matches;
};

/**
 * Show print loading indicator
 */
export const showPrintLoading = () => {
  const loader = document.createElement('div');
  loader.id = 'print-loader';
  loader.innerHTML = `
    <div style="
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0,0,0,0.7);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 99999;
    ">
      <div style="
        background: white;
        padding: 30px;
        border-radius: 10px;
        text-align: center;
      ">
        <div class="spinner" style="
          border: 3px solid #f3f3f3;
          border-top: 3px solid #3498db;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          animation: spin 1s linear infinite;
          margin: 0 auto 15px;
        "></div>
        <p style="margin: 0; font-size: 14px;">Mempersiapkan dokumen...</p>
      </div>
    </div>
    <style>
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    </style>
  `;
  document.body.appendChild(loader);
};

/**
 * Hide print loading indicator
 */
export const hidePrintLoading = () => {
  const loader = document.getElementById('print-loader');
  if (loader) {
    loader.remove();
  }
};

/**
 * All-in-one print handler with loading
 * @param {string} documentId - ID of the printable element
 * @param {object} options - Configuration options
 */
export const handlePrintWithLoading = (documentId, options = {}) => {
  const {
    showLoading = true,
    loadingDelay = 300,
    onSuccess = null,
    onError = null
  } = options;

  try {
    if (showLoading) showPrintLoading();

    prepareDocumentForPrint(documentId);

    setTimeout(() => {
      if (showLoading) hidePrintLoading();

      printDocument(
        documentId,
        null,
        () => {
          cleanupAfterPrint(documentId);
          if (onSuccess) onSuccess();
        }
      );
    }, loadingDelay);
  } catch (error) {
    console.error('Print error:', error);
    if (showLoading) hidePrintLoading();
    if (onError) onError(error);
  }
};

const printUtils = {
  printDocument,
  exportToWord,
  formatDate,
  formatDateWithLocation,
  generateDocumentNumber,
  prepareDocumentForPrint,
  cleanupAfterPrint,
  handlePrintWithLoading,
  showPrintLoading,
  hidePrintLoading
};

// eslint-disable-next-line import/no-anonymous-default-export
export default printUtils;
